<template lang="pug">
.flex.flex-col.gap-1
  .label(v-if="label") {{ label }}
  .flex.items-center.gap-2.flex-wrap
    Select(
      v-model="localHour"
      :options="getHourOptions"
      optionLabel="label"
      optionValue="value"
      :showClear="false"
    )
    span.select-none.text-canvas-400.font-medium :
    Select(
      v-model="localMinute"
      :options="minuteOptions"
      optionLabel="label"
      optionValue="value"
      :showClear="false"
    )
    SelectButton(
      v-model="localPeriod"
      :options="periodOptions"
      optionLabel="label"
      optionValue="value"
      size="small"
      :allowEmpty="false"
    )
    Tag.ml-1(v-if="formattedTime" :value="formattedTime" severity="info")
  FormField(:name="name")
</template>

<script setup>
import { ref, computed, watch, onMounted, inject } from 'vue'
import { FormField } from '@primevue/forms'

const props = defineProps({
  name:  { type: String, required: true },
  label: { type: String, required: false },
  date:  { type: [String, Date, Object], default: null },
  allow_blank_hour: { type: Boolean, default: false },
})

defineOptions({ inheritAttrs: false })

const $pcForm = inject('$pcForm', null)

const localHour   = ref(null)
const localMinute = ref(null)
const localPeriod = ref(null)

const formattedTime = computed(() => {
  if (!localHour.value || !localMinute.value || !localPeriod.value) return null
  return `${localHour.value}:${localMinute.value} ${localPeriod.value.toLowerCase()}`
})

const getHourOptions = computed(() => {
  return props.allow_blank_hour ? [{ value: null, label: '--'  },...hourOptions] : hourOptions
})

function to24Hour(hour, period) {
  const h = parseInt(hour)
  if (isNaN(h)) return 0
  if (period === 'PM' && h !== 12) return h + 12
  if (period === 'AM' && h === 12) return 0
  return h
}

function assemble() {
  if (!localHour.value || !localMinute.value || !localPeriod.value || !props.date) return
  const d = dayjs(props.date)
  if (!d.isValid()) return
  const assembled = d
    .hour(to24Hour(localHour.value, localPeriod.value))
    .minute(parseInt(localMinute.value))
    .second(0)
    .millisecond(0)
  $pcForm?.setFieldValue(props.name, assembled.format())
}

watch([localHour, localMinute, localPeriod, () => props.date], assemble)

onMounted(() => {
  const existingValue = $pcForm?.fields?.[props.name]?.states?.value
  if (!existingValue) return
  const d = dayjs(existingValue)
  if (!d.isValid()) return
  localHour.value   = String(d.hour() % 12 || 12)
  localMinute.value = d.format('mm')
  localPeriod.value = d.hour() >= 12 ? 'PM' : 'AM'
})

const hourOptions = [
  { value: '1',  label: '01'  },
  { value: '2',  label: '02'  },
  { value: '3',  label: '03'  },
  { value: '4',  label: '04'  },
  { value: '5',  label: '05'  },
  { value: '6',  label: '06'  },
  { value: '7',  label: '07'  },
  { value: '8',  label: '08'  },
  { value: '9',  label: '09'  },
  { value: '10', label: '10' },
  { value: '11', label: '11' },
  { value: '12', label: '12' },
]

const minuteOptions = [
  { value: '00', label: '00' },
  { value: '15', label: '15' },
  { value: '30', label: '30' },
  { value: '45', label: '45' },
  { value: '59', label: '59' },
]

const periodOptions = [
  { value: 'AM', label: 'AM' },
  { value: 'PM', label: 'PM' },
]
</script>
